import cx from 'classnames';
import { forwardRef } from 'react';

import { ConditionalView } from '../../utils/ConditionalView';
import { Input, Icon, IconButton, Button } from '../../atoms';
import styles from './Search.module.scss';
import usePresenter from './Search.presenter';
import type { SearchProps } from './Search.types';

/**
 * Search component with auto and manual search modes
 * 
 * @param props - The search component props
 * @param ref - Forward ref to the input element
 * @returns Search component
 */
const Search = forwardRef<HTMLInputElement, SearchProps>((props: SearchProps, ref) => {
  const {
    showSubmitButton = true,
    showClearButton = true,
    className,
  } = props;

  const {
    controlledValue,
    isSearching,
    searchDisabled,
    handleInputChange,
    handleSubmit,
    handleClear,
    handleKeyDown,
    showClearButton: shouldShowClearButton,
    isAutoStyle,
    isButtonStyle,
    variantProps,
    restProps: inputProps,
  } = usePresenter(props);

  return (
    <div 
      className={cx(styles.search, className)}
      {...variantProps}
    >
      <Input
        {...inputProps}
        ref={ref}
        className={styles.searchInput}
        value={controlledValue}
        onValueChange={handleInputChange}
        onKeyDown={handleKeyDown}
        leadingIcon={
          <ConditionalView condition={isAutoStyle}>
            <Icon
              className={styles.searchIcon}
              icon="Search"
              style="Outlined"
              size="Medium"
            />
          </ConditionalView>
        }
        trailingIcon={
          <ConditionalView condition={shouldShowClearButton && showClearButton}>
            <IconButton
              className={styles.searchIcon}
              type="Ghost"
              size="Small"
              icon={<Icon icon="CloseDelete" style="Outlined" size="Small" />}
              onClick={handleClear}
              disabled={isSearching}
              aria-label="Clear search"
            />
          </ConditionalView>
        }
        placeholder={props.placeholder || 'Search...'}
        disabled={props.disabled || isSearching}
      />
      <ConditionalView condition={isButtonStyle && showSubmitButton}>
        <Button
          className={styles.searchButton}
          type="Ghost"
          size="Small"
          onClick={handleSubmit}
          disabled={searchDisabled}
          alignContent='SpaceBetween'
          aria-label="Search"
        >
          <Icon icon="Search" style="Outlined" size="Small" />
        </Button>
      </ConditionalView>
    </div>
  );
});

export default Search;